/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

import { NgClass } from "@angular/common";
import {
	Component,
	ElementRef,
	EventEmitter,
	forwardRef,
	input,
	Input,
	OnDestroy,
	OnInit,
	Output,
	Renderer2,
	viewChild
} from "@angular/core";
import {
	AbstractControl,
	ControlValueAccessor,
	FormsModule,
	NG_VALIDATORS,
	NG_VALUE_ACCESSOR,
	ValidationErrors,
	Validator
} from "@angular/forms";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatSelect, MatSelectModule } from "@angular/material/select";
import { debounceTime, distinctUntilChanged, Subject, takeUntil } from "rxjs";
import { SearchInSelectDirective } from "../../directives/search-in-select.directive";
import { PaginatedSelectionModel } from "../../models/ui/paginated-selection.model";
import { SelectOption } from "../../models/ui/select-option.model";

@Component({
	selector: "globalictcc-dropdown",
	standalone: true,
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => DropdownComponent),
			multi: true
		},
		{
			provide: NG_VALIDATORS,
			useExisting: forwardRef(() => DropdownComponent),
			multi: true
		}
	],
	imports: [
		MatFormFieldModule,
		MatSelectModule,
		MatInputModule,
		FormsModule,
		NgClass,
		SearchInSelectDirective
	],
	templateUrl: "./dropdown.component.html",
	styleUrl: "./dropdown.component.scss"
})
export class DropdownComponent implements OnInit, ControlValueAccessor, Validator, OnDestroy {
	public readonly allowSearch = input(false);

	@Input() public label: string = "";
	@Input() public multi: boolean = false;
	@Input() public disabled: boolean = false;
	@Input() public isALLOptionVisible: boolean = false;
	@Input() public isHuge: boolean = false;

	/**
	 * The list of options to display. When `paginated` is true and `paginatedAllSelected` is
	 * true, any newly added options (from a "Load more" fetch) are auto-selected if they are
	 * not in the excluded set.
	 */
	@Input()
	public set options(value: SelectOption[]) {
		this._options = value ?? [];

		if (this.paginated && this.initialAllSelected && !this._initialLoadDone) {
			// Prime "all selected" state the first time real options arrive — never again,
			// so the user's explicit Select/Deselect All choices are preserved across
			// search resets and Load More fetches.
			this._initialLoadDone = true;
			this.paginatedAllSelected = true;
			this._excludedIds.clear();
		}

		if (this.paginated && this.paginatedAllSelected && this._options.length) {
			// Auto-select all newly loaded options that haven't been manually excluded
			const allNonExcluded = this._options
				.map(o => o.value)
				.filter(v => !this._excludedIds.has(v));
			this.selection = allNonExcluded;
			this.allSelected = allNonExcluded.length === this._options.length;
			this._onChange(this.selection);
			this._emitPaginatedSelection();
		}
	}

	public get options(): SelectOption[] {
		return this._options;
	}

	/** Enables paginated + server-side-search behaviour. Implicitly activates the search input. */
	@Input() public paginated: boolean = false;
	/**
	 * When true (and `paginated` is true), the dropdown enters "all selected" state as soon as
	 * the first page of options arrives — equivalent to the user clicking "Select All".
	 * Use this when the backend default is to return data for all items.
	 */
	@Input() public initialAllSelected: boolean = false;
	/** Whether there are more pages to load. Shows the "Load More" button when true. */
	@Input() public hasMore: boolean = false;
	/** Set to true while the next page is being fetched to show a loading indicator. */
	@Input() public isLoadingMore: boolean = false;
	/**
	 * When true (and `paginated` is true), renders "Select All" and "Deselect All" controls
	 * at the top of the panel. "Select All" sets the semantic "all including unloaded items"
	 * flag; individual deselections are tracked as excluded IDs.
	 */
	@Input() public showSelectAllControls: boolean = false;

	@Output() public handleSelectionChange = new EventEmitter<string | string[]>();
	/**
	 * Emits the debounced (300 ms) search term when `paginated` is true.
	 * An empty string is emitted when the dropdown closes, signalling a reset to the first page.
	 */
	@Output() public searchChange = new EventEmitter<string>();
	/** Emitted when the user clicks "Load More" to request the next page of options. */
	@Output() public loadMore = new EventEmitter<void>();
	/**
	 * Emitted whenever the paginated selection changes (Select All, Deselect All, or individual
	 * option clicks). Carries the full semantic model the backend needs.
	 */
	@Output() public paginatedSelectionChange = new EventEmitter<PaginatedSelectionModel>();
	/** Emitted when the panel opens (true) or closes (false). */
	@Output() public panelOpenChange = new EventEmitter<boolean>();

	public isSelectAllPressed: boolean = false;
	public selection: string | string[] = "";
	public allSelected: boolean = false;
	public isDisabled: boolean = false;
	public isInvalid: boolean = false;
	/** True when the user has clicked "Select All" — includes items not yet loaded. */
	public paginatedAllSelected: boolean = false;

	/** Number of items the user manually deselected after clicking "Select All". */
	public get excludedCount(): number {
		return this._excludedIds.size;
	}

	private _options: SelectOption[] = [];
	private _excludedIds: Set<string> = new Set();
	/** Guards the one-time initialAllSelected priming so user interactions are never overridden. */
	private _initialLoadDone: boolean = false;
	private _searchSubject = new Subject<string>();
	private _destroy$ = new Subject<void>();
	private _intersectionObserver: IntersectionObserver | null = null;

	private readonly _matSelect = viewChild(MatSelect);
	private readonly _loadMoreSentinel = viewChild<ElementRef<HTMLElement>>("loadMoreSentinel");

	constructor(
		private _renderer: Renderer2,
		private _el: ElementRef
	) {}

	ngOnInit() {
		const selectArrow = this._el.nativeElement.querySelector(".mat-mdc-select-arrow-wrapper");

		if (selectArrow?.firstChild) {
			const icon = this._renderer.createElement("span");

			this._renderer.addClass(icon, "icon-chevron-down");
			this._renderer.setStyle(icon, "transition", "all 0.3s");
			this._renderer.removeChild(selectArrow, selectArrow.firstChild);
			this._renderer.appendChild(selectArrow, icon);
		}

		this._searchSubject
			.pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this._destroy$))
			.subscribe(term => this.searchChange.emit(term));
	}

	ngOnDestroy(): void {
		this._detachInfiniteScroll();
		this._destroy$.next();
		this._destroy$.complete();
	}

	/**
	 * Handles the change event when the dropdown is opened or closed.
	 * Rotates the dropdown arrow icon based on the open state.
	 *
	 * @param isOpened - A boolean indicating whether the dropdown is opened (true) or closed (false).
	 */
	public onOpenedChange(isOpened: boolean) {
		const selectArrow = this._el.nativeElement.querySelector(".mat-mdc-select-arrow-wrapper");
		const icon = selectArrow?.firstChild;

		if (icon) {
			this._renderer.setStyle(icon, "transform", `rotate(${isOpened ? 180 : 0}deg)`);
		}

		if (this.paginated) {
			if (isOpened) {
				this._attachInfiniteScroll();
			} else {
				this._detachInfiniteScroll();
				// Push "" through the subject so distinctUntilChanged's memory is reset to "".
				// This ensures that if the user searches for the same term on the next open,
				// it is not swallowed. The debounce will forward "" to the parent after 300ms,
				// which triggers a first-page reload while the panel is already closed.
				this._searchSubject.next("");
			}
		}

		this.panelOpenChange.emit(isOpened);
		this._onTouched();
	}

	/**
	 * Attaches a scroll listener via an Intersection Observer, using a phantom DOM sentinel element at the end of the options list as the trigger. When this element enters the viewport, the next page is requested via the `loadMore` emitter.
	 */
	private _attachInfiniteScroll(): void {
		const sentinel = this._loadMoreSentinel()?.nativeElement;
		const panel = this._matSelect()?.panel?.nativeElement as HTMLElement | undefined;
		if (!sentinel || !panel) return;

		this._intersectionObserver = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting && this.hasMore && !this.isLoadingMore) {
					this.loadMore.emit();
				}
			},
			{ root: panel, threshold: 0.1 }
		);

		this._intersectionObserver.observe(sentinel);
	}

	private _detachInfiniteScroll(): void {
		this._intersectionObserver?.disconnect();
		this._intersectionObserver = null;
	}

	/**
	 * Receives the raw search term from the directive (on every keystroke) and
	 * pushes it to the debounce Subject so `searchChange` fires at most once per 300 ms.
	 */
	public onPaginatedSearch(value: string): void {
		this._searchSubject.next(value);
	}

	/**

	 * Triggered when the user clicks the "Select All" control inside the panel.
	 * Marks every currently loaded option as selected and sets the semantic "all" flag so that
	 * options added later (via Load More) are also auto-selected. Emits the selection model.
	 */
	public onSelectAll(event: Event): void {
		event.stopPropagation();
		this.paginatedAllSelected = true;
		this._excludedIds.clear();
		this.selection = this.options.map(o => o.value);
		this.allSelected = true;
		this._onChange(this.selection);
		this.handleSelectionChange.emit(this.selection);
		this._emitPaginatedSelection();
	}

	/** Toggles between selecting all options and deselecting all options. */
	public onToggleSelectAll(event: Event): void {
		if (this.paginatedAllSelected && this._excludedIds.size === 0) {
			this.onDeselectAll(event);
		} else {
			this.onSelectAll(event);
		}
	}

	/**
	 * Triggered when the user clicks the "Deselect All" control inside the panel.
	 * Clears the entire selection and resets the "all" flag. Emits the selection model.
	 */
	public onDeselectAll(event: Event): void {
		event.stopPropagation();
		this.paginatedAllSelected = false;
		this._excludedIds.clear();
		this.selection = [];
		this.allSelected = false;
		this._onChange(this.selection);
		this.handleSelectionChange.emit(this.selection);
		this._emitPaginatedSelection();
	}

	/** Builds and emits the PaginatedSelectionModel based on current state. */
	private _emitPaginatedSelection(): void {
		this.paginatedSelectionChange.emit({
			allSelected: this.paginatedAllSelected,
			selectedIds: this.paginatedAllSelected ? [] : (this.selection as string[]),
			excludedIds: this.paginatedAllSelected ? [...this._excludedIds] : []
		});
	}

	/**
	 * Handles the change in value of the dropdown.
	 *
	 * @param event - The new value of the dropdown, which can be a string or an array of strings.
	 *                If the dropdown is in multi-select mode and the first element of the array is "all",
	 *                it sets the `isSelectAllPressed` flag to true.
	 */
	public onValueChange(event: string | string[]) {
		this.isSelectAllPressed = this.multi && event[0] === "all";
	}

	/**
	 * Handles the selection change event for the dropdown component.
	 *
	 * @param event - The selection change event from the MatSelect component.
	 *
	 * This method updates the selection based on whether the dropdown allows multiple selections (`multi`).
	 *
	 * - If `multi` is true and "Select All" is pressed, it either clears the selection or selects all options.
	 * - If `multi` is false, it updates the selection with the new value.
	 *
	 * It then emits the updated selection and triggers the change handler.
	 */
	public onSelectionChange(selection: string | string[]) {
		// ── Paginated mode: track excluded IDs when in "all selected" state ──
		if (this.paginated && this.paginatedAllSelected) {
			const newSelection = selection as string[];
			const prevSelection = this.selection as string[];

			// Items removed by the user → add to excluded set
			for (const prev of prevSelection) {
				if (!newSelection.includes(prev)) {
					this._excludedIds.add(prev);
				}
			}
			// Items re-selected by the user → remove from excluded set
			for (const curr of newSelection) {
				this._excludedIds.delete(curr);
			}

			this.selection = newSelection;
			this.allSelected =
				newSelection.length === this.options.length && this._excludedIds.size === 0;
			this._onChange(this.selection);
			this.handleSelectionChange.emit(this.selection);
			this._emitPaginatedSelection();
			this._onTouched();
			return;
		}

		// ── Standard (non-paginated, or paginated without allSelected) ──
		const updatedSelection = this.multi
			? (selection as string[]).filter((selection: string) => selection !== "all")
			: selection;

		if (this.multi && this.allSelected && this.isSelectAllPressed) {
			this.selection = [];
			this.allSelected = false;
		} else if (this.multi && !this.allSelected && this.isSelectAllPressed) {
			this.selection = this.options.map(option => option.value);
			this.allSelected = true;
		} else {
			if (this.paginated && !this.paginatedAllSelected && this.multi) {
				// Preserve selections from other search pages that are not in the current options.
				// MatSelect only knows about rendered options, so items selected while the search
				// term was different would otherwise be lost when the user picks a new option.
				const currentOptionValues = new Set(this._options.map(o => o.value));
				const hiddenSelections = (this.selection as string[]).filter(
					s => !currentOptionValues.has(s)
				);
				this.selection = [...hiddenSelections, ...(updatedSelection as string[])];
			} else {
				this.selection = updatedSelection;
			}
			this.allSelected =
				this.multi && (updatedSelection as string[]).length === this.options.length;
		}

		if (this.paginated) {
			// Paginated but not in allSelected mode: track selectedIds
			this._emitPaginatedSelection();
		}

		this.handleSelectionChange.emit(this.selection);
		this._onChange(this.selection);
		this._onTouched();
	}

	/**
	 * Writes a value to the dropdown component.
	 *
	 * @param value - The value to be written. It can be a string or an array of strings.
	 */
	public writeValue(value: string | string[]) {
		this.selection = value;
	}

	/**
	 * Sets the disabled state of the dropdown component.
	 *
	 * @param isDisabled - A boolean value indicating whether the dropdown should be disabled.
	 */
	public setDisabledState(isDisabled: boolean) {
		this.isDisabled = isDisabled;
	}

	/**
	 * A private callback function that is triggered when the value changes.
	 * It accepts a single argument which can be either a string or an array of strings.
	 *
	 * @param value - The new value, which can be a string or an array of strings.
	 */
	private _onChange: (value: string | string[]) => void = () => {};

	/**
	 * Registers a callback function that is called when the value of the dropdown changes.
	 *
	 * @param fn - The callback function that takes the new value of the dropdown as its argument.
	 *             The value can be a string or an array of strings.
	 */
	public registerOnChange(fn: (value: string | string[]) => void) {
		this._onChange = fn;
	}

	/**
	 * A private method that is assigned a no-op function.
	 * This method is intended to be called when the component is touched.
	 * It can be overridden to provide custom behavior when the component is interacted with.
	 */
	private _onTouched: () => void = () => {};

	/**
	 * Registers a callback function that will be called when the control is touched.
	 * This method is part of the ControlValueAccessor interface, which allows the component
	 * to act as a form control in Angular forms.
	 *
	 * @param fn - The callback function to register.
	 */
	public registerOnTouched(fn: () => void) {
		this._onTouched = fn;
	}

	/**
	 * Validates the given form control and updates the `isInvalid` property.
	 *
	 * @param control - The form control to validate.
	 * @returns An object containing validation errors if any, otherwise null.
	 */
	public validate(control: AbstractControl): ValidationErrors | null {
		this.isInvalid = control.invalid && !!control.errors && (control.touched || control.dirty);

		return null;
	}
}
